import { useState } from "react";
import { useTranslation } from "react-i18next";
import { NotebookPen } from "lucide-react";
import { Button } from "./ui/button";
import SaveToNotebookModal from "./SaveToNotebookModal";
import { useAuth } from "../context/AuthContext";
import { requireAuth } from "../lib/authGate";
import { toast } from "sonner";

/**
 * Universal "💾 הכנס למחברת" / "Save to Notebook" trigger button.
 *
 * Usage:
 *   <SaveToNotebookButton payload={{
 *     source_type: "verse",
 *     source_book: "Genesis",
 *     source_chapter: 1,
 *     source_verse: 1,
 *     source_ref_he: "בראשית א:א",
 *     content_he: "בראשית ברא...",
 *   }} />
 *
 * Variants:
 *   • "icon"   — just the notebook icon, used in dense UIs (verse rows,
 *                commentary header rows). Tooltip via title attr.
 *   • "chip"   — small pill with icon + label. For wider surfaces.
 */
export default function SaveToNotebookButton({
  payload,
  variant = "chip",
  size = "sm",
  className = "",
  testIdSuffix = "",
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const label = t("notebook.save_to_notebook");

  const handleClick = (e) => {
    // Prevent both the parent <Link>'s navigation AND any wrapping
    // card click handler from firing — this button is sometimes
    // rendered inside a clickable card.
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!requireAuth("notebook")) return;
    setOpen(true);
  };

  if (variant === "icon") {
    return (
      <>
        <button
          onClick={handleClick}
          title={label}
          aria-label={label}
          data-testid={`save-notebook-icon${testIdSuffix ? `-${testIdSuffix}` : ""}`}
          className={`inline-flex items-center justify-center h-7 w-7 rounded-sm text-muted-foreground hover:text-gold hover:bg-gold/10 transition-colors ${className}`}
        >
          <NotebookPen className="h-3.5 w-3.5" strokeWidth={1.5} />
        </button>
        <SaveToNotebookModal open={open} onClose={() => setOpen(false)} payload={payload} />
      </>
    );
  }

  return (
    <>
      <Button
        onClick={handleClick}
        variant="ghost"
        size={size}
        data-testid={`save-notebook-chip${testIdSuffix ? `-${testIdSuffix}` : ""}`}
        className={`text-muted-foreground hover:text-gold hover:bg-gold/10 min-h-[40px] ${className}`}
      >
        <NotebookPen className="h-3.5 w-3.5 ms-1.5" strokeWidth={1.5} />
        {label}
      </Button>
      <SaveToNotebookModal open={open} onClose={() => setOpen(false)} payload={payload} />
    </>
  );
}
